import React, {useEffect, useRef, useState} from 'react'
import {
 Controller,
 useFieldArray,
 useForm,
} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {Button, KTSVG, Model, TextField} from 'helpers'
import {
 IQualityParameterDefinition,
 QualityParameterType,
} from 'models/CropMaster'
import {fetchQualityParameters} from 'utils/api/cropMasterService'
import QualityParameterSelect from './QualityParameterSelect'

export const MAX_QUALITY_PARAMETERS = 6

export type IEditableQualityParameter = {
 id?: number
 quality_parameter_id: number
 quality_parameter_name: string
 type?: QualityParameterType
 unit?: string | null
 default_value: Record<string, unknown> | null
}

type IModalRow = {
 parameter_id: string
 range_from: string
 range_to: string
 free_text: string
 dropdown_value: string
}

const EMPTY_ROW: IModalRow = {
 parameter_id: '',
 range_from: '',
 range_to: '',
 free_text: '',
 dropdown_value: '',
}

type RawDropdownOption =
 | {
    id?: number | string
    name?: string
    value?: number | string
    label?: string
   }
 | string
 | number
 | null
 | undefined

const getDropdownOptions = (
 options: RawDropdownOption[] = []
) =>
 options.map((option, index) => {
  const opt =
   option && typeof option === 'object' ? option : undefined
  return {
   id: opt?.id ?? opt?.value ?? index,
   name:
    opt?.name ?? opt?.label ?? String(opt?.value ?? option),
  }
 })

const buildRowFromParameter = (
 parameter: IEditableQualityParameter
): IModalRow => {
 const row: IModalRow = {
  parameter_id: String(parameter.quality_parameter_id),
  range_from: '',
  range_to: '',
  free_text: '',
  dropdown_value: '',
 }
 const value = parameter.default_value
 if (!value) return row

 const hasRangeShape =
  value.min !== undefined || value.max !== undefined
 const hasLabelShape =
  value.label !== undefined &&
  value.label !== null &&
  value.label !== ''
 const hasPlainValue =
  value.value !== undefined && value.value !== null

 const isRange =
  parameter.type === 'RANGE' ||
  (!parameter.type && hasRangeShape)
 const isDropdown =
  parameter.type === 'DROPDOWN' ||
  (!parameter.type && !hasRangeShape && hasLabelShape)
 const isFreeText =
  parameter.type === 'FREE_TEXT' ||
  (!parameter.type &&
   !hasRangeShape &&
   !hasLabelShape &&
   hasPlainValue)

 if (isRange) {
  if (value.min !== undefined && value.min !== null) {
   row.range_from = String(value.min)
  }
  if (value.max !== undefined && value.max !== null) {
   row.range_to = String(value.max)
  }
 } else if (isDropdown && hasPlainValue) {
  row.dropdown_value = String(value.value)
 } else if (isFreeText && hasPlainValue) {
  row.free_text = String(value.value)
 }
 return row
}

type IProps = {
 show: boolean
 excludedParameterIds: number[]
 close: () => void
 onSave: (parameters: IEditableQualityParameter[]) => void
 setLoader: (newState: boolean) => void
 editingParameter?: IEditableQualityParameter | null
}

const QualityParameterModal = ({
 show,
 excludedParameterIds,
 close,
 onSave,
 setLoader,
 editingParameter = null,
}: IProps) => {
 const {t} = useTranslation()
 const isEditing = Boolean(editingParameter)
 const [parameterDefinitions, setParameterDefinitions] =
  useState<IQualityParameterDefinition[]>([])
 const [isLoadingDefinitions, setIsLoadingDefinitions] =
  useState(false)
 const {
  control,
  register,
  watch,
  setValue,
  handleSubmit,
  setError,
  clearErrors,
  formState: {errors},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
  defaultValues: {
   parameters: [
    editingParameter
     ? buildRowFromParameter(editingParameter)
     : EMPTY_ROW,
   ] as IModalRow[],
  },
 })

 const {fields: rowFields, append: appendRow} =
  useFieldArray({
   control,
   name: 'parameters',
  })

 useEffect(() => {
  const loadParameterDefinitions = async () => {
   try {
    setLoader(true)
    setIsLoadingDefinitions(true)
    const res = await fetchQualityParameters()
    setParameterDefinitions(Array.isArray(res) ? res : [])
   } catch (error) {
    console.error(
     'Failed to load quality parameters:',
     error
    )
    setParameterDefinitions([])
   } finally {
    setIsLoadingDefinitions(false)
    setLoader(false)
   }
  }
  void loadParameterDefinitions()
 }, [])

 const watchedRows = watch('parameters')

 const rowDefinitions = watchedRows.map((row) =>
  parameterDefinitions.find(
   (def) => def.id === Number(row.parameter_id)
  )
 )

 const selectedIdsInModal = watchedRows
  .map((row) => Number(row.parameter_id))
  .filter(Boolean)

 const prevParameterIdsRef = useRef<string[]>([])
 useEffect(() => {
  const currentIds = watchedRows.map(
   (row) => row.parameter_id
  )
  currentIds.forEach((id, index) => {
   const prevId = prevParameterIdsRef.current[index]
   if (prevId !== undefined && prevId !== id) {
    setValue(`parameters.${index}.range_from`, '')
    setValue(`parameters.${index}.range_to`, '')
    setValue(`parameters.${index}.free_text`, '')
    setValue(`parameters.${index}.dropdown_value`, '')
   }
  })
  prevParameterIdsRef.current = currentIds
 })

 const totalConfigured =
  excludedParameterIds.length + rowFields.length
 const canAddMoreRows =
  totalConfigured < MAX_QUALITY_PARAMETERS
 const allRowsHaveParameter = watchedRows.every((row) =>
  Boolean(row.parameter_id)
 )

 const getOptionsForRow = (index: number) =>
  parameterDefinitions.filter((def) => {
   if (excludedParameterIds.includes(def.id)) return false
   const usedInOtherRow = selectedIdsInModal.some(
    (id, i) => i !== index && id === def.id
   )
   return !usedInOtherRow
  })

 const handleAddRow = () => {
  appendRow({...EMPTY_ROW})
 }

 const handleClearRow = (index: number) => {
  setValue(`parameters.${index}.parameter_id`, '')
  setValue(`parameters.${index}.range_from`, '')
  setValue(`parameters.${index}.range_to`, '')
  setValue(`parameters.${index}.free_text`, '')
  setValue(`parameters.${index}.dropdown_value`, '')
 }

 const handleRangeStep = (
  index: number,
  field: 'range_from' | 'range_to',
  direction: 1 | -1
 ) => {
  const fieldName = `parameters.${index}.${field}` as const
  const current = watch(fieldName)
  const currentNum =
   current === '' ||
   current === undefined ||
   Number.isNaN(Number(current))
    ? 0
    : Number(current)
  setValue(fieldName, String(currentNum + direction), {
   shouldValidate: true,
  })
 }

 const submitAll = (formData: {
  parameters: IModalRow[]
 }) => {
  clearErrors()
  let hasError = false

  formData.parameters.forEach((row, index) => {
   const def = parameterDefinitions.find(
    (d) => d.id === Number(row.parameter_id)
   )
   if (!def) {
    setError(`parameters.${index}.parameter_id`, {
     message: t('helperText.plsSelect'),
    })
    hasError = true
    return
   }
   if (def.type === 'RANGE') {
    if (!row.range_from) {
     setError(`parameters.${index}.range_from`, {
      message: t('helperText.plsEnter'),
     })
     hasError = true
    }
    if (!row.range_to) {
     setError(`parameters.${index}.range_to`, {
      message: t('helperText.plsEnter'),
     })
     hasError = true
    }
   } else if (def.type === 'FREE_TEXT') {
    if (!row.free_text?.trim()) {
     setError(`parameters.${index}.free_text`, {
      message: t('helperText.plsEnter'),
     })
     hasError = true
    }
   } else if (def.type === 'DROPDOWN') {
    if (!row.dropdown_value) {
     setError(`parameters.${index}.dropdown_value`, {
      message: t('helperText.plsSelect'),
     })
     hasError = true
    }
   }
  })

  if (hasError) return

  const parameters: IEditableQualityParameter[] =
   formData.parameters.map((row) => {
    const def = parameterDefinitions.find(
     (d) => d.id === Number(row.parameter_id)
    )
    let default_value: Record<string, unknown> | null = null
    if (def?.type === 'RANGE') {
     default_value = {
      min: Number(row.range_from),
      max: Number(row.range_to),
     }
    } else if (def?.type === 'FREE_TEXT') {
     default_value = {value: row.free_text}
    } else if (def?.type === 'DROPDOWN') {
     const normalizedOptions = getDropdownOptions(
      def.options ?? []
     )
     const selectedOption = normalizedOptions.find(
      (option) =>
       String(option.id) === String(row.dropdown_value)
     )
     default_value = {
      value: selectedOption?.id ?? row.dropdown_value,
      label: selectedOption?.name ?? '',
     }
    }
    return {
     ...(editingParameter?.id && {id: editingParameter.id}),
     quality_parameter_id: Number(row.parameter_id),
     quality_parameter_name: def?.name ?? '',
     type: def?.type,
     unit: def?.unit,
     default_value,
    }
   })

  onSave(parameters)
  close()
 }

 return (
  <Model
   show={show}
   showHeader={false}
   closeBtnClass='absolute right-4 top-2 !bg-[#E6EBF0] !rounded-3xl w-7 h-7 !text-[#33393D]'
   header=''
   close={close}
   modelClass='md:w-[600px]'
   modelContainerClass='!rounded-2xl flex flex-col max-h-[80vh]'
   modelBodyClass='p-10 flex flex-col flex-1 min-h-0'
  >
   <div className='w-full h-full flex flex-col min-h-0'>
    <p className='text-[#171B1F] text-left font-bold text-2xl flex-shrink-0'>
     {t('cropMaster.addCrop.qualityParametersModalTitle')}
    </p>
    <p className='text-[#5A6066] text-sm mt-1 mb-5 flex-shrink-0'>
     {t(
      'cropMaster.addCrop.qualityParametersModalSubtitle'
     )}
    </p>

    <div className='flex-1 min-h-0 overflow-y-auto pr-1'>
     <div className='flex flex-col gap-4'>
      {rowFields.map((field, index) => {
       const rowDef = rowDefinitions[index]
       return (
        <div
         key={field.id}
         className='bg-[#F5F8FA] border border-[#E6EBF0] rounded-lg p-4'
        >
         <Controller
          control={control}
          name={`parameters.${index}.parameter_id`}
          rules={{
           required: t('helperText.plsSelect'),
          }}
          render={({field}) => (
           <QualityParameterSelect
            id={`parameters.${index}.parameter_id`}
            label={t('cropMaster.addCrop.parameterName')}
            required
            searchable={false}
            isLoading={isLoadingDefinitions}
            value={field.value}
            options={getOptionsForRow(index)}
            onChange={(selectedId) =>
             field.onChange(String(selectedId))
            }
            onBlur={field.onBlur}
            placeholder={t(
             'cropMaster.addCrop.selectParameter'
            )}
            noOptionsText={t(
             'cropMaster.addCrop.noParametersFound'
            )}
            error={
             errors?.parameters?.[index]?.parameter_id
              ?.message as string | undefined
            }
            endAdornment={
             !isEditing &&
             rowDef && (
              <button
               type='button'
               onClick={() => handleClearRow(index)}
               className='flex items-center justify-center w-8 h-8 rounded-lg hover:bg-[#FDE9EB] flex-shrink-0'
              >
               <KTSVG
                path='/media/icons/trash.svg'
                svgClassName='w-4 h-4'
               />
              </button>
             )
            }
           />
          )}
         />
         {!isLoadingDefinitions &&
          parameterDefinitions.length === 0 && (
           <p className='text-[#CC293F] text-xs mt-2'>
            {t('cropMaster.addCrop.noParametersFound')}
           </p>
          )}

         {rowDef && (
          <div className='mt-3 pt-3 border-t border-[#E6EBF0]'>
           {rowDef.type !== 'RANGE' && (
            <p className='padding-0 text-[#4E83E4] text-xs !font-medium mb-2'>
             {t('cropMaster.addCrop.measure')}
            </p>
           )}
           {rowDef.type === 'RANGE' && (
            <div className='grid md:grid-cols-2 gap-6'>
             {(
              [
               ['range_from', 'acceptableFrom'],
               ['range_to', 'acceptableTo'],
              ] as const
             ).map(([field, labelKey]) => (
              <div key={field}>
               <p className='text-[#5A6066] text-[12px] font-medium mb-2'>
                {t(`cropMaster.addCrop.${labelKey}`)}
               </p>
               <TextField
                type='number'
                step='any'
                className='!text-xl [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none !pr-24'
                wrapperClassName='relative w-full'
                inputWrapperClassName='!py-3'
                id={`parameters.${index}.${field}`}
                name={`parameters.${index}.${field}`}
                register={register(
                 `parameters.${index}.${field}`
                )}
                placeholder={t('helperText.enter')}
                errors={errors}
                endAdornment={
                 <div className='absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2'>
                  <div className='flex flex-col rounded overflow-hidden bg-[#F0F2F5]'>
                   <button
                    type='button'
                    tabIndex={-1}
                    onClick={() =>
                     handleRangeStep(index, field, 1)
                    }
                    className='flex items-center justify-center w-5 h-[13px] hover:bg-[#E2E6EB]'
                   >
                    <i className='fa fa-caret-up text-[10px] leading-none text-[#80868C]' />
                   </button>
                   <button
                    type='button'
                    tabIndex={-1}
                    onClick={() =>
                     handleRangeStep(index, field, -1)
                    }
                    className='flex items-center justify-center w-5 h-[13px] hover:bg-[#E2E6EB]'
                   >
                    <i className='fa fa-caret-down text-[10px] leading-none text-[#80868C]' />
                   </button>
                  </div>
                  {rowDef.unit && (
                   <span className='text-[#80868C] text-sm'>
                    {rowDef.unit}
                   </span>
                  )}
                 </div>
                }
               />
              </div>
             ))}
            </div>
           )}

           {rowDef.type === 'FREE_TEXT' && (
            <TextField
             type='text'
             label={t('cropMaster.addCrop.parameterValue')}
             labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
             wrapperClassName='relative w-full'
             className={rowDef.unit ? '!pr-16' : undefined}
             id={`parameters.${index}.free_text`}
             name={`parameters.${index}.free_text`}
             register={register(
              `parameters.${index}.free_text`
             )}
             placeholder={t('helperText.enter')}
             errors={errors}
             endAdornment={
              rowDef.unit ? (
               <span className='absolute bottom-[10px] right-4 text-[#80868C] text-sm'>
                {rowDef.unit}
               </span>
              ) : undefined
             }
            />
           )}

           {rowDef.type === 'DROPDOWN' && (
            <Controller
             control={control}
             name={`parameters.${index}.dropdown_value`}
             rules={{
              required: t('helperText.plsSelect'),
             }}
             render={({field}) => (
              <QualityParameterSelect
               id={`parameters.${index}.dropdown_value`}
               label={t(
                'cropMaster.addCrop.parameterValue'
               )}
               value={field.value}
               options={getDropdownOptions(
                rowDef.options ?? []
               )}
               onChange={(selectedId) =>
                field.onChange(String(selectedId))
               }
               onBlur={field.onBlur}
               placeholder={t('helperText.select')}
               error={
                errors?.parameters?.[index]?.dropdown_value
                 ?.message as string | undefined
               }
              />
             )}
            />
           )}
          </div>
         )}
        </div>
       )
      })}
     </div>
    </div>

    {!isEditing &&
     canAddMoreRows &&
     allRowsHaveParameter && (
      <button
       type='button'
       onClick={handleAddRow}
       className='flex items-center gap-1 mt-4 text-sm text-[#4E83E4] font-semibold underline underline-offset-2 flex-shrink-0'
      >
       + {t('cropMaster.addCrop.addMoreParameter')}
      </button>
     )}

    <div className='flex items-center justify-end gap-3 mt-7 flex-shrink-0'>
     <div className='flex items-center gap-3'>
      <Button
       type='button'
       disabled={false}
       onClick={close}
       color='blue'
       className='text-center py-2.5 px-8 bg-white
        !rounded-lg text-base text-[#33393D] font-semibold border-[#CED8E0] hover:!bg-[#F5F8FA]'
      >
       {t('common.cancel')}
      </Button>
      <Button
       type='button'
       disabled={false}
       onClick={handleSubmit(submitAll)}
       color='blue'
       className='disabled:text-white text-center py-2.5 px-8 bg-[#4E83E4]
        !rounded-lg text-base text-white font-semibold border-[#4E83E4]'
      >
       {isEditing
        ? t('common.save')
        : t('cropMaster.addCrop.saveAndContinue')}
      </Button>
     </div>
    </div>
   </div>
  </Model>
 )
}

export default QualityParameterModal
